<template>
  <div>
    <div
      v-if="showMobileFilters"
      class="drawer-overlay"
      @click="closeDrawer"
    ></div>

    <div
      :class="[
        'filters-container w-full flex flex-col font-rubik mb-2 mobile-drawer',
        { open: showMobileFilters }
      ]"
    >
      <div class="sections-bottom-border flex justify-between">
        <h3 class="p-3 component-name">Filters</h3>
        <button class="p-3 lg:hidden" @click="closeDrawer">X</button>
      </div>

      <div class="sections-bottom-border" v-if="!isRentalsPage">
        <div class="p-3">
          <h4 class="filter-name">Condition</h4>
          <div v-for="(value, label) in filteredConditions" :key="value">
            <label class="filter-checkbox">
              <input
                type="checkbox"
                @change="update('condition', value)"
                :checked="filters.condition.includes(value)"
              />
              {{ label }}
            </label>
          </div>
        </div>
      </div>

      <div class="sections-bottom-border">
        <div class="p-3">
          <h4 class="filter-name">Size</h4>
          <div v-for="(label, size) in sizes" :key="size">
            <label class="filter-radio">
              <input
                type="radio"
                name="size"
                @change="update('size', size)"
                :value="size"
                :checked="filters.size === size"
              />
              {{ label }}
            </label>
          </div>
        </div>
      </div>

      <div class="p-3" v-if="!isRentalsPage && Object.keys(usageType).length > 1">
        <h4 class="filter-name">Usage Type</h4>
        <div v-for="(option, label) in usageType" :key="option">
          <label class="filter-radio">
            <span class="flex items-center gap-2">
              <input
                type="radio"
                name="usage-type"
                @change="update('usageType', option)"
                :checked="filters.usageType === option"
              />
              <icon
                :type="option"
                style="max-width: 16px !important; max-height: 16px !important;"
              />
              <label>{{ label }}</label>
            </span>
          </label>
        </div>
      </div>

      <div class="mobile-selected-filters lg:hidden p-3 flex flex-wrap gap-2">
        <button
          v-for="filter in selectedFilters"
          :key="filter.text"
          @click="removeFilter(filter)"
          class="filter-button"
        >
          <span class="text-[10px]">{{ filter.text }}</span>
          <img
            class="inline pl-2"
            src="@/images/partners-page/clear.svg"
            :alt="`Clear ${filter.text} filter`"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from './icon.vue'

const SIZES = {
  'ODD': 'Odd',
  '10': '10\'',
  '15': '15\'',
  '20': '20\'',
  '20HC': '20\' High Cube',
  '24': '24\'',
  '25': '25\'',
  '30': '30\'',
  '40': '40\'',
  '40HC': '40\' High Cube',
  '45': '45\'',
  '45HC': '45\' High Cube',
  '53HC': '53\' High Cube',
  '60': '60\''
}

const SHIPPING_CONDITIONS = [
  'one_trip',
  'iicl',
  'iicl-5',
  'cargo_worthy'
]

export default {
  components: {
    Icon
  },
  props: {
    products: {
      type: Array,
      required: true
    }
  },
  data() {
    const usageType = { 'Domestic Storage': 'domestic_storage' }

    if (this.products.some((product) => SHIPPING_CONDITIONS.includes(product.condition))) {
      usageType['Shipping Export'] = 'shipping_export'
    }

    if (this.products.some((product) => product.config === 'working_refrigerated')) {
      usageType['Refrigerated Storage'] = 'working_refrigerated'
    }

    if (this.products.some((product) => product.config === 'non_working_refrigerated')) {
      usageType['Insulated Storage'] = 'non_working_refrigerated'
    }

    return {
      sizes: [],
      filters: {
        size: null,
        condition: [],
        usageType: null
      },
      conditions: {
        'One Trip (Like New)': 'one_trip',
        'Cargo Worthy': 'cargo_worthy',
        'Refurbished': 'refurbished',
        'WWT': 'wind_water_tight'
      },
      usageType: usageType,
      showMobileFilters: false,
      selectedFilters: []
    }
  },
  computed: {
    isRentalsPage() {
      const path = window.location.pathname.toLowerCase()
      return path.includes('rentals')
    },
    filteredConditions() {
      const productConditions = this.products.map((p) => p.condition)
      const uniqueConditions = [...new Set(productConditions)]
      const filtered = {}
      for (const label in this.conditions) {
        const value = this.conditions[label]
        if (uniqueConditions.includes(value)) {
          filtered[label] = value
        }
      }
      return filtered
    }
  },
  created() {
    this.$bus.$on('removeFilter', (val) => {
      this.removeFilter(val)
    })
    this.$bus.$on('toggleMobileFilters', () => {
      this.showMobileFilters = !this.showMobileFilters
    })
  },
  mounted() {
    this.products.forEach(product => {
      this.sizes[product.size] = SIZES[product.size] || product.size
    })

    this.sizes = Object.keys(this.sizes).sort().reduce((obj, key) => {
      obj[key] = this.sizes[key]
      return obj
    }, {})
  },
  beforeDestroy() {
    this.$bus.$off('removeFilter')
    this.$bus.$off('toggleMobileFilters')
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        const filters = []

        if (this.filters.size) {
          filters.push({
            type: 'size',
            value: this.filters.size,
            text: `${SIZES[this.filters.size]} container`
          })
        }

        this.filters.condition.forEach(condition => {
          filters.push({
            type: 'condition',
            value: condition,
            text: `${SIZES[this.filters.size] || ''} ${this.getConditionLabel(condition)} container`.trim()
          })
        })

        if (this.filters.usageType) {
          const label = Object.keys(this.usageType).find(key => this.usageType[key] === this.filters.usageType) || this.filters.usageType
          filters.push({
            type: 'usageType',
            value: this.filters.usageType,
            text: label
          })
        }

        this.selectedFilters = filters
        this.$bus.$emit('selectedFilters', filters)
      }
    }
  },
  methods: {
    update(filterType, value) {
      if (filterType === 'size') {
        this.filters.size = value
      } else if (filterType === 'usageType') {
        this.filters.usageType = value
      } else {
        if (this.filters[filterType].includes(value)) {
          this.filters[filterType] = this.filters[filterType].filter(item => item !== value)
        } else {
          this.filters[filterType].push(value)
        }
      }

      this.emitFilteredProducts()
    },

    removeFilter(filter) {
      if (filter.type === 'size') {
        this.filters.size = null
      } else if (filter.type === 'usageType') {
        this.filters.usageType = null
      } else {
        this.filters[filter.type] = this.filters[filter.type].filter(item => item !== filter.value)
      }

      this.emitFilteredProducts()
    },

    emitFilteredProducts() {
      const filtered = this.products.filter(product => {
        const matchesCondition = (this.filters.condition.length === 0 || this.filters.condition.includes(product.condition))
        const matchesSize = (this.filters.size === null || this.filters.size === product.size)
        const matchesUsageType = (this.filters.usageType === null || this.filterByUsageType(product))

        return matchesCondition && matchesSize && matchesUsageType
      })

      this.$bus.$emit('filteredProducts', filtered)
    },

    filterByUsageType(product) {
      if (this.filters.usageType === 'domestic_storage') {
        return !['working_refrigerated', 'non_working_refrigerated'].includes(product.config)
      } else if (this.filters.usageType === 'shipping_export') {
        return SHIPPING_CONDITIONS.includes(product.condition)
      } else if (this.filters.usageType === 'working_refrigerated') {
        return product.config === 'working_refrigerated'
      } else if (this.filters.usageType === 'non_working_refrigerated') {
        return product.config === 'non_working_refrigerated'
      } else {
        return true
      }
    },

    getConditionLabel(value) {
      return Object.keys(this.conditions).find(key => this.conditions[key] === value) || value
    },

    closeDrawer() {
      this.showMobileFilters = false
    }
  }
}
</script>

<style scoped>
.filters-container {
  border: 1px solid #E2E1DE;
  background-color: white;
  border-radius: 12px;
  width: 288px;
}
.sections-bottom-border {
  border-bottom: 1px solid #E2E1DE;
}
.component-name {
  font-size: 18px;
  font-weight: 500;
  line-height: 27px;
  letter-spacing: -0.0125em;
  color: black;
}
.filter-name {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.0125em;
  color: black;
}

/* mobile */
@media (max-width: 1024px) {
  .filters-container {
    display: none;
  }
  .mobile-drawer {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    z-index: 50;
    transform: translateX(100%);
    transition: transform 0.3s ease;
    overflow-y: auto;
  }
  .mobile-drawer.open {
    display: flex;
    transform: translateX(0);
  }
  .drawer-close {
    display: block;
    position: absolute;
    top: 16px;
    right: 16px;
    font-size: 18px;
    background: none;
    border: none;
    cursor: pointer;
  }
}
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: 49;
}
.drawer-close {
  display: none;
}

.filter-button{
  background: #EEECE9;
  color: black;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.0125em;
  height: 40px;
  margin-left: 1px;
  border: 1px solid #EEECE9;
  border-radius: 12px;
  padding: 0px 15px;
}
</style>
